import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
    Smartphone, 
    Monitor, 
    Apple, 
    Chrome,
    Share,
    Plus,
    MoreVertical,
    Download,
    Globe,
    CheckCircle,
    ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Install() {
    const navigate = useNavigate();
    const [language, setLanguage] = useState('it');
    const [deviceType, setDeviceType] = useState('desktop');
    const [appUrl, setAppUrl] = useState('');

    useEffect(() => {
        // Rileva il tipo di dispositivo
        const userAgent = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(userAgent)) {
            setDeviceType('ios');
        } else if (/android/.test(userAgent)) {
            setDeviceType('android');
        } else {
            setDeviceType('desktop');
        }

        // Ottieni l'URL dell'app
        setAppUrl(window.location.origin);
    }, []);

    const texts = {
        it: {
            title: 'Installa Smart Reporting',
            subtitle: 'Accedi rapidamente dall\'icona sulla tua Home',
            scanQR: 'Scansiona il QR Code',
            scanDesc: 'Usa la fotocamera del tuo smartphone per scansionare il codice',
            iosTitle: 'Installazione su iPhone/iPad',
            iosSteps: [
                'Apri Safari e vai all\'indirizzo dell\'app',
                'Tocca l\'icona di condivisione (quadrato con freccia)',
                'Scorri e tocca "Aggiungi a Home"',
                'Conferma toccando "Aggiungi"'
            ],
            androidTitle: 'Installazione su Android',
            androidSteps: [
                'Apri Chrome e vai all\'indirizzo dell\'app',
                'Tocca il menu (tre puntini in alto a destra)',
                'Tocca "Installa app" o "Aggiungi a Home"',
                'Conferma l\'installazione'
            ],
            yourDevice: 'Il tuo dispositivo',
            otherDevice: 'Altro dispositivo',
            printQR: 'Stampa QR Code',
            backToApp: 'Torna all\'app',
            benefits: 'Vantaggi dell\'installazione',
            benefit1: 'Accesso rapido dalla Home',
            benefit2: 'Esperienza fullscreen',
            benefit3: 'Funziona anche offline',
            benefit4: 'Notifiche push'
        },
        en: {
            title: 'Install Smart Reporting',
            subtitle: 'Quick access from your Home screen icon',
            scanQR: 'Scan QR Code',
            scanDesc: 'Use your smartphone camera to scan the code',
            iosTitle: 'Install on iPhone/iPad',
            iosSteps: [
                'Open Safari and go to the app URL',
                'Tap the share icon (square with arrow)',
                'Scroll and tap "Add to Home Screen"',
                'Confirm by tapping "Add"'
            ],
            androidTitle: 'Install on Android',
            androidSteps: [
                'Open Chrome and go to the app URL',
                'Tap the menu (three dots top right)',
                'Tap "Install app" or "Add to Home screen"',
                'Confirm the installation'
            ],
            yourDevice: 'Your device',
            otherDevice: 'Other device',
            printQR: 'Print QR Code',
            backToApp: 'Back to app',
            benefits: 'Installation benefits',
            benefit1: 'Quick Home access',
            benefit2: 'Fullscreen experience',
            benefit3: 'Works offline',
            benefit4: 'Push notifications'
        }
    };

    const t = texts[language];

    const IOSInstructions = ({ highlight = false }) => (
        <div className={`p-6 rounded-xl border transition-all ${
            highlight 
                ? 'bg-gradient-to-br from-gray-900/90 to-gray-800/90 border-[#00FF41]/50 shadow-[0_0_30px_rgba(0,255,65,0.15)]' 
                : 'bg-white/5 border-white/10'
        }`}>
            <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${highlight ? 'bg-[#00FF41]/20' : 'bg-white/10'}`}>
                    <Apple className={`w-6 h-6 ${highlight ? 'text-[#00FF41]' : 'text-white/70'}`} />
                </div>
                <div>
                    <h3 className="font-bold text-white">{t.iosTitle}</h3>
                    {highlight && <span className="text-xs text-[#00FF41]">{t.yourDevice}</span>}
                </div>
            </div>
            <ol className="space-y-3">
                {t.iosSteps.map((step, index) => (
                    <li key={`ios-step-${index}`} className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                            highlight ? 'bg-[#00FF41] text-black' : 'bg-white/20 text-white'
                        }`}>
                            {index + 1}
                        </span>
                        <span className="text-white/80 text-sm pt-0.5">{step}</span>
                        {index === 1 && <Share className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />}
                        {index === 2 && <Plus className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />}
                    </li>
                ))}
            </ol>
        </div>
    );

    const AndroidInstructions = ({ highlight = false }) => (
        <div className={`p-6 rounded-xl border transition-all ${
            highlight 
                ? 'bg-gradient-to-br from-gray-900/90 to-gray-800/90 border-[#00FF41]/50 shadow-[0_0_30px_rgba(0,255,65,0.15)]' 
                : 'bg-white/5 border-white/10'
        }`}>
            <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${highlight ? 'bg-[#00FF41]/20' : 'bg-white/10'}`}>
                    <Chrome className={`w-6 h-6 ${highlight ? 'text-[#00FF41]' : 'text-white/70'}`} />
                </div>
                <div>
                    <h3 className="font-bold text-white">{t.androidTitle}</h3>
                    {highlight && <span className="text-xs text-[#00FF41]">{t.yourDevice}</span>}
                </div>
            </div>
            <ol className="space-y-3">
                {t.androidSteps.map((step, index) => (
                    <li key={`android-step-${index}`} className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                            highlight ? 'bg-[#00FF41] text-black' : 'bg-white/20 text-white'
                        }`}>
                            {index + 1}
                        </span>
                        <span className="text-white/80 text-sm pt-0.5">{step}</span>
                        {index === 1 && <MoreVertical className="w-4 h-4 text-green-400 flex-shrink-0 mt-1" />}
                        {index === 2 && <Download className="w-4 h-4 text-green-400 flex-shrink-0 mt-1" />}
                    </li>
                ))}
            </ol>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#050505] text-white">
            {/* Header */}
            <div className="bg-[#0A0A0A] border-b border-white/10">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <button 
                        onClick={() => navigate('/login')}
                        className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="text-sm">{t.backToApp}</span>
                    </button>
                    
                    {/* Language Switcher */}
                    <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                        <button
                            onClick={() => setLanguage('it')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                language === 'it' 
                                    ? 'bg-[#00FF41] text-black' 
                                    : 'text-white/60 hover:text-white'
                            }`}
                        >
                            IT
                        </button>
                        <button
                            onClick={() => setLanguage('en')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                language === 'en' 
                                    ? 'bg-[#00FF41] text-black' 
                                    : 'text-white/60 hover:text-white'
                            }`}
                        >
                            EN
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Title Section */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white mb-4 shadow-[0_0_30px_rgba(0,255,65,0.2)]">
                        <img 
                            src="/smart-info-logo.png" 
                            alt="Smart Info Logo" 
                            className="w-16 h-16 object-contain"
                        />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">{t.title}</h1>
                    <p className="text-white/60">{t.subtitle}</p>
                </div>

                {/* Desktop: QR Code Section */}
                {deviceType === 'desktop' && (
                    <div className="mb-10">
                        <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 border border-[#00FF41]/30 rounded-2xl p-8 text-center shadow-[0_0_50px_rgba(0,255,65,0.1)]">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <Monitor className="w-5 h-5 text-[#00FF41]" />
                                <h2 className="text-xl font-bold">{t.scanQR}</h2>
                            </div>
                            <p className="text-white/60 text-sm mb-6">{t.scanDesc}</p>
                            
                            <div className="inline-block p-4 bg-white rounded-2xl shadow-[0_0_30px_rgba(0,255,65,0.2)]">
                                <QRCodeSVG 
                                    value={appUrl}
                                    size={200}
                                    level="H"
                                    includeMargin={true}
                                    bgColor="#ffffff"
                                    fgColor="#000000"
                                />
                            </div>
                            
                            <p className="mt-4 text-xs text-white/40 font-mono">{appUrl}</p>
                            
                            <button 
                                onClick={() => window.print()}
                                className="mt-6 px-6 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm transition-colors"
                            >
                                {t.printQR}
                            </button>
                        </div>
                    </div>
                )}

                {/* Mobile: Show QR smaller */}
                {deviceType !== 'desktop' && (
                    <div className="mb-8 flex justify-center">
                        <div className="inline-block p-3 bg-white rounded-xl">
                            <QRCodeSVG 
                                value={appUrl}
                                size={120}
                                level="H"
                                bgColor="#ffffff"
                                fgColor="#000000"
                            />
                        </div>
                    </div>
                )}

                {/* Instructions Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-10">
                    <IOSInstructions highlight={deviceType === 'ios'} />
                    <AndroidInstructions highlight={deviceType === 'android'} />
                </div>

                {/* Benefits Section */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-[#00FF41]" />
                        {t.benefits}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[t.benefit1, t.benefit2, t.benefit3, t.benefit4].map((benefit, i) => (
                            <div key={`benefit-${i}`} className="flex items-center gap-2 text-sm text-white/70">
                                <div className="w-2 h-2 rounded-full bg-[#00FF41]" />
                                {benefit}
                            </div>
                        ))}
                    </div>
                </div>

                {/* App URL for mobile */}
                {deviceType !== 'desktop' && (
                    <div className="mt-6 p-4 bg-[#0A0A0A] border border-white/10 rounded-xl">
                        <div className="flex items-center gap-3">
                            <Globe className="w-5 h-5 text-[#00FF41]" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-white/40 mb-1">URL App</p>
                                <p className="text-sm font-mono text-white truncate">{appUrl}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .print-qr, .print-qr * {
                        visibility: visible;
                    }
                    .print-qr {
                        position: absolute;
                        left: 50%;
                        top: 50%;
                        transform: translate(-50%, -50%);
                    }
                }
            `}</style>
        </div>
    );
}
